import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useJoyride, STATUS, ACTIONS, EVENTS } from "react-joyride";
import { Button } from "@mui/material";
import { useNotify } from "hooks";
import { COLORS } from "components/consts";

import { CustomTooltip, SkipTourDialog } from "./components";
import { OnboardingGuideContext } from "./context";
import { OnboardingGuideProps, OnboardingGuideRef } from "./types";
import { TOUR_OPTIONS, TOUR_STYLES } from "./styles";

export const OnboardingGuide = forwardRef<
  OnboardingGuideRef,
  OnboardingGuideProps
>(({
  enableStartButton = false,
  enableSkipButton = false,
  continuous = true,
  autoStart = true,
  buttonTitle = "Start Onboarding Guide",
  customFinalNotification = "Onboarding guide completed!",
  steps,
  onStepChange,
  onStepAfterChange,
  onTourEnd,
}, ref) => {
  const notify = useNotify();
  const [confirmSkipOpen, setConfirmSkipOpen] = useState(false);
  const pausedStepRef = useRef(0);

  const { controls, on, state, failures, Tour } = useJoyride({
    continuous,
    steps,
    options: TOUR_OPTIONS,
    styles: TOUR_STYLES,
    tooltipComponent: CustomTooltip,
  });

  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  useImperativeHandle(ref, () => ({
    stop: () => controlsRef.current.stop(),
    resume: (step: number) => {
      controlsRef.current.start(step);
      controlsRef.current.open();
    },
  }));

  const contextValue = useMemo(
    () => ({ enableSkipButton }),
    [enableSkipButton],
  );

  const handleStartTour = useCallback(() => {
    controls.start();
    if (autoStart) {
      controls.open();
    }
  }, [controls, autoStart]);

  const handleStartTourRef = useRef(handleStartTour);
  handleStartTourRef.current = handleStartTour;

  const onStepChangeRef = useRef(onStepChange);
  onStepChangeRef.current = onStepChange;

  const onStepAfterChangeRef = useRef(onStepAfterChange);
  onStepAfterChangeRef.current = onStepAfterChange;

  const onTourEndRef = useRef(onTourEnd);
  onTourEndRef.current = onTourEnd;

  useEffect(
    () =>
      on(EVENTS.STEP_BEFORE, (data) => {
        onStepChangeRef.current?.(data.index);
      }),
    [on],
  );

  useEffect(
    () =>
      on(EVENTS.STEP_AFTER, (data) => {
        onStepAfterChangeRef.current?.(data.index, data.action);
        if (data.action === ACTIONS.CLOSE) {
          pausedStepRef.current = data.index;
          controls.stop();
          setConfirmSkipOpen(true);
        }
      }),
    [on, controls],
  );

  const handleConfirmSkip = () => {
    setConfirmSkipOpen(false);
    onTourEndRef.current?.();
  };

  const handleCancelSkip = () => {
    setConfirmSkipOpen(false);
    controls.start(pausedStepRef.current);
    controls.open();
  };

  useEffect(() => {
    if (state.status === STATUS.FINISHED) {
      notify.success(customFinalNotification);
      onTourEndRef.current?.();
    }
  }, [state.status, notify, customFinalNotification]);

  useEffect(() => {
    if (!enableStartButton && autoStart) {
      handleStartTourRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (failures.length > 0) {
      console.error("Onboarding guide failures:", failures);
    }
  }, [failures, notify]);

  return (
    <OnboardingGuideContext.Provider value={contextValue}>
      {enableStartButton && (
        <Button
          variant="contained"
          onClick={handleStartTour}
          sx={{
            width: "fit-content",
            backgroundColor: COLORS.Romance,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { backgroundColor: COLORS.Romance },
          }}
        >
          {buttonTitle}
        </Button>
      )}
      {Tour}
      <SkipTourDialog
        open={confirmSkipOpen}
        onSkip={handleConfirmSkip}
        onContinue={handleCancelSkip}
      />
    </OnboardingGuideContext.Provider>
  );
});
